/**
 * OneBase ERP API Service
 * Servicio para interactuar con la API del ERP OneBase
 */

import api from './api';

// Derivar URL base para OneBase desde la baseURL actual de `api`
// Si api.baseURL es '/api/content', entonces API_BASE_URL debe ser '/api/onebase'
const API_BASE_URL = (() => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  // Remover '/content' y agregar '/onebase'
  return baseURL.replace(/\/content\/?$/i, '/onebase');
})();

// ============================================
// LÍNEAS DE NEGOCIO
// ============================================

export const lineaNegocioService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/lineas-negocio/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/lineas-negocio/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/lineas-negocio/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/lineas-negocio/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/lineas-negocio/${id}/`);
  },
};

// ============================================
// CLIENTES
// ============================================

export const clienteService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/clientes/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/clientes/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/clientes/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/clientes/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/clientes/${id}/`);
  },
};

// ============================================
// CATEGORÍAS DE PRESUPUESTO
// ============================================

export const categoriaService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/categorias-presupuesto/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/categorias-presupuesto/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/categorias-presupuesto/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/categorias-presupuesto/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/categorias-presupuesto/${id}/`);
  },
};

// ============================================
// PROYECTOS
// ============================================

export const proyectoService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/proyectos/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/proyectos/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/proyectos/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/proyectos/${id}/`, data);
  },

  getRentabilidad: (id) => {
    return api.get(`${API_BASE_URL}/proyectos/${id}/rentabilidad/`);
  },

  getComparativaPresupuesto: (id) => {
    return api.get(`${API_BASE_URL}/proyectos/${id}/comparativa_presupuesto/`);
  },

  getAvanceAcumulado: (proyectoIds = []) => {
    if (proyectoIds.length === 0) {
      // Sin filtros - todos los proyectos
      return api.get(`${API_BASE_URL}/proyectos/avance_acumulado/`);
    }
    // Con filtros
    const params = new URLSearchParams();
    proyectoIds.forEach(id => params.append('proyectos[]', id));
    return api.get(`${API_BASE_URL}/proyectos/avance_acumulado/?${params.toString()}`);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/proyectos/${id}/`);
  },
};

// ============================================
// PRESUPUESTOS DE VENTA
// ============================================

export const presupuestoVentaService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/presupuestos-venta/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/presupuestos-venta/${id}/`, data);
  },

  cambiarEstado: (id, estado) => {
    return api.post(`${API_BASE_URL}/presupuestos-venta/${id}/cambiar_estado/`, { estado });
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/presupuestos-venta/${id}/`);
  },

  duplicar: (id) => {
    return api.post(`${API_BASE_URL}/presupuestos-venta/${id}/duplicar/`);
  },

  // Endpoints para seguimiento de ejecución
  getResumenEjecucion: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta/${id}/resumen_ejecucion/`);
  },

  getItemsPorEstado: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta/${id}/items_por_estado/`);
  },
};

// ============================================
// ÍTEMS PRESUPUESTO VENTA
// ============================================

export const itemPresupuestoVentaService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/items-presupuesto-venta/`, {
      params: filters,
    });
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/items-presupuesto-venta/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/items-presupuesto-venta/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/items-presupuesto-venta/${id}/`);
  },
};

// ============================================
// PRESUPUESTOS DE COSTO
// ============================================

export const presupuestoCostoService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo/${id}/`);
  },

  getItems: (presupuestoId) => {
    return api.get(`${API_BASE_URL}/items-presupuesto-costo/`, {
      params: { presupuesto: presupuestoId },
    });
  },

  getPartidas: async (presupuestoId) => {
    // Obtener las áreas del presupuesto
    const areasResponse = await api.get(`${API_BASE_URL}/presupuestos-costo-areas/`, {
      params: { presupuesto_costo: presupuestoId },
    });

    const areas = areasResponse.data?.results || areasResponse.data || [];

    if (areas.length === 0) {
      return { data: [] };
    }

    // Obtener todas las partidas de todas las áreas
    const partidasPromises = areas.map(area =>
      api.get(`${API_BASE_URL}/presupuestos-costo-partidas/`, {
        params: { area: area.id },
      })
    );

    const partidasResponses = await Promise.all(partidasPromises);

    // Combinar todas las partidas en un solo array
    const todasLasPartidas = partidasResponses.flatMap(response =>
      response.data?.results || response.data || []
    );

    return { data: todasLasPartidas };
  },

  getRecursos: (partidaId) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo-recursos/`, {
      params: { partida: partidaId },
    });
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/presupuestos-costo/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/presupuestos-costo/${id}/`, data);
  },

  cambiarEstado: (id, estado) => {
    return api.post(`${API_BASE_URL}/presupuestos-costo/${id}/cambiar_estado/`, { estado });
  },

  getTotales: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo/${id}/totales/`);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/presupuestos-costo/${id}/`);
  },
};

// ============================================
// ÍTEMS PRESUPUESTO COSTO
// ============================================

export const itemPresupuestoCostoService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/items-presupuesto-costo/`, {
      params: filters,
    });
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/items-presupuesto-costo/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/items-presupuesto-costo/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/items-presupuesto-costo/${id}/`);
  },
};

// ============================================
// PROVEEDORES
// ============================================

export const proveedorService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/proveedores/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/proveedores/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/proveedores/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/proveedores/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/proveedores/${id}/`);
  },
};

// ============================================
// CLASES DE RECURSOS (CONTABILIDAD)
// ============================================

const CONTABILIDAD_API_BASE = (() => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  // Remover '/content' y agregar '/contabilidad'
  return baseURL.replace(/\/content\/?$/i, '/contabilidad');
})();

export const claseRecursoService = {
  getAll: (filters = {}) => {
    return api.get(`${CONTABILIDAD_API_BASE}/clases-recursos/`, {
      params: filters,
    });
  },

  getTree: (filters = {}) => {
    return api.get(`${CONTABILIDAD_API_BASE}/clases-recursos/tree/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${CONTABILIDAD_API_BASE}/clases-recursos/${id}/`);
  },

  create: (data) => {
    return api.post(`${CONTABILIDAD_API_BASE}/clases-recursos/`, data);
  },

  update: (id, data) => {
    return api.patch(`${CONTABILIDAD_API_BASE}/clases-recursos/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${CONTABILIDAD_API_BASE}/clases-recursos/${id}/`);
  },
};

// ============================================
// PLAN DE CUENTAS (CONTABILIDAD)
// ============================================

export const planCuentaService = {
  getAll: (filters = {}) => {
    return api.get(`${CONTABILIDAD_API_BASE}/plan-cuentas/`, {
      params: filters,
    });
  },

  getTree: (filters = {}) => {
    return api.get(`${CONTABILIDAD_API_BASE}/plan-cuentas/tree/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${CONTABILIDAD_API_BASE}/plan-cuentas/${id}/`);
  },

  create: (data) => {
    return api.post(`${CONTABILIDAD_API_BASE}/plan-cuentas/`, data);
  },

  update: (id, data) => {
    return api.patch(`${CONTABILIDAD_API_BASE}/plan-cuentas/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${CONTABILIDAD_API_BASE}/plan-cuentas/${id}/`);
  },
};

// ============================================
// ÓRDENES DE COMPRA
// ============================================

export const ordenCompraService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/ordenes-compra/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/ordenes-compra/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/ordenes-compra/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/ordenes-compra/${id}/`, data);
  },

  cambiarEstado: (id, estado) => {
    return api.post(`${API_BASE_URL}/ordenes-compra/${id}/cambiar_estado/`, { estado });
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/ordenes-compra/${id}/`);
  },
};

// ============================================
// DETALLES ORDEN DE COMPRA
// ============================================

export const detalleOrdenCompraService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/detalles-orden-compra/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/detalles-orden-compra/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/detalles-orden-compra/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/detalles-orden-compra/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/detalles-orden-compra/${id}/`);
  },
};

// ============================================
// RENDICIONES
// ============================================

export const rendicionService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/rendiciones/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/rendiciones/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/rendiciones/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/rendiciones/${id}/`, data);
  },

  aprobar: (id) => {
    return api.post(`${API_BASE_URL}/rendiciones/${id}/aprobar/`, {});
  },

  rechazar: (id, motivo) => {
    return api.post(`${API_BASE_URL}/rendiciones/${id}/rechazar/`, { motivo });
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/rendiciones/${id}/`);
  },
};

// ============================================
// DETALLES RENDICIÓN
// ============================================

export const detalleRendicionService = {
  create: (data) => {
    return api.post(`${API_BASE_URL}/detalles-rendicion/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/detalles-rendicion/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/detalles-rendicion/${id}/`);
  },
};

// Alias para mantener compatibilidad
export const categoriaPresupuestoService = categoriaService;

// ============================================
// ASIGNACIÓN PRESUPUESTOS VENTA A ÁREAS
// ============================================

export const presupuestoVentaAreaService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta-areas/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-venta-areas/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/presupuestos-venta-areas/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/presupuestos-venta-areas/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/presupuestos-venta-areas/${id}/`);
  },
};

// ============================================
// ASIGNACIÓN PRESUPUESTOS COSTO A ÁREAS
// ============================================

export const presupuestoCostoAreaService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo-areas/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/presupuestos-costo-areas/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/presupuestos-costo-areas/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/presupuestos-costo-areas/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/presupuestos-costo-areas/${id}/`);
  },
};

// ============================================
// IMPUTACIONES DE COSTO
// ============================================

export const imputacionCostoService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/imputaciones-costo/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/imputaciones-costo/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/imputaciones-costo/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/imputaciones-costo/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/imputaciones-costo/${id}/`);
  },
};

// ============================================
// RECURSOS
// ============================================

export const recursoService = {
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/recursos/`, {
      params: filters,
    });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/recursos/${id}/`);
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/recursos/`, data);
  },

  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/recursos/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/recursos/${id}/`);
  },
};

// ============================================
// EQUIPOS
// ============================================

export const equipoService = {
  getAll: (params) => {
    return api.get(`${API_BASE_URL}/equipos/`, { params });
  },

  getById: (id) => {
    return api.get(`${API_BASE_URL}/equipos/${id}/`);
  },

  getSimple: (params) => {
    return api.get(`${API_BASE_URL}/equipos/simple/`, { params });
  },

  create: (data) => {
    return api.post(`${API_BASE_URL}/equipos/`, data);
  },

  update: (id, data) => {
    return api.put(`${API_BASE_URL}/equipos/${id}/`, data);
  },

  delete: (id) => {
    return api.delete(`${API_BASE_URL}/equipos/${id}/`);
  },

  addMember: (equipoId, data) => {
    return api.post(`${API_BASE_URL}/equipos/${equipoId}/add_member/`, data);
  },

  removeMember: (equipoId, data) => {
    return api.post(`${API_BASE_URL}/equipos/${equipoId}/remove_member/`, data);
  },

  getCostoDetalle: (equipoId) => {
    return api.get(`${API_BASE_URL}/equipos/${equipoId}/costo_detalle/`);
  },
};

export default {
  lineaNegocioService,
  clienteService,
  categoriaService,
  categoriaPresupuestoService,
  proyectoService,
  presupuestoVentaService,
  itemPresupuestoVentaService,
  presupuestoCostoService,
  itemPresupuestoCostoService,
  proveedorService,
  claseRecursoService,
  planCuentaService,
  ordenCompraService,
  detalleOrdenCompraService,
  rendicionService,
  detalleRendicionService,
  presupuestoVentaAreaService,
  presupuestoCostoAreaService,
  imputacionCostoService,
  recursoService,
  equipoService,
};
